import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  ACCESS_CATALOG,
} from "../../auth/permissions";

import {
  changeRbacUserRole,
  createRbacUser,
  fetchRbacRoles,
  fetchRbacUsers,
  putRbacRole,
} from "../../auth/rbacApi";


const EMPTY_USER = {
  name: "",
  email: "",
  role: "empleados",
};


const EMPTY_ROLE = {
  key: "",
  name: "",
};


function normalizeRoleKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9_-]/g, "");
}


function getInitials(value) {

  const text =
    String(value || "")
      .trim();

  if (!text) {
    return "US";
  }


  const parts =
    text
      .split(/\s+/)
      .filter(Boolean);


  if (parts.length === 1) {

    return parts[0]
      .slice(0, 2)
      .toUpperCase();
  }


  return (
    parts[0][0] +
    parts[
      parts.length - 1
    ][0]
  ).toUpperCase();
}


function formatDate(value) {

  if (!value) {
    return "Sin acceso registrado";
  }


  const date =
    new Date(value);


  if (
    Number.isNaN(
      date.getTime()
    )
  ) {

    return value;
  }


  return date.toLocaleString(
    "es-MX",
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  );
}


function roleLabel(
  role,
  roles
) {

  return (
    roles.find(
      (item) =>
        item.role === role
    )?.name ||
    role ||
    "Sin rol"
  );
}


function Toast({
  type,
  message,
  onClose,
}) {

  if (!message) {
    return null;
  }


  return (
    <div
      className={`scc-toast scc-toast-${type}`}
      role="status"
    >
      <div className="scc-toast-icon">
        {type === "error"
          ? "!"
          : "✓"}
      </div>

      <div className="scc-toast-message">
        {message}
      </div>

      <button
        type="button"
        className="scc-toast-close"
        onClick={onClose}
        aria-label="Cerrar"
      >
        ×
      </button>
    </div>
  );
}


function Drawer({
  open,
  title,
  eyebrow,
  children,
  onClose,
}) {

  if (!open) {
    return null;
  }


  return (
    <div className="scc-overlay">

      <button
        type="button"
        className="scc-overlay-dismiss"
        onClick={onClose}
        aria-label="Cerrar panel"
      />

      <aside
        className="scc-drawer"
      >

        <div className="scc-drawer-head">

          <div>

            <div className="scc-eyebrow">
              {eyebrow}
            </div>

            <h2 className="scc-drawer-title">
              {title}
            </h2>

          </div>


          <button
            type="button"
            className="scc-icon-button"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>

        </div>


        <div className="scc-drawer-body">
          {children}
        </div>

      </aside>

    </div>
  );
}


function RoleModal({
  user,
  roles,
  onClose,
  onApply,
  busy,
}) {

  const [
    selected,
    setSelected,
  ] =
    useState(
      user?.role ||
      ""
    );


  if (!user) {
    return null;
  }


  return (
    <div className="scc-overlay scc-overlay-centered">

      <button
        type="button"
        className="scc-overlay-dismiss"
        onClick={onClose}
        aria-label="Cerrar"
      />

      <section className="scc-modal">

        <div className="scc-modal-head">

          <div>

            <div className="scc-eyebrow">
              AUTORIZACION
            </div>

            <h2 className="scc-modal-title">
              Cambiar nivel de acceso
            </h2>

          </div>


          <button
            type="button"
            className="scc-icon-button"
            onClick={onClose}
            aria-label="Cerrar"
          >
            ×
          </button>

        </div>


        <div className="scc-person-summary">

          <div className="scc-avatar scc-avatar-large">
            {getInitials(
              user.name ||
              user.email
            )}
          </div>

          <div>

            <strong className="scc-person-name">
              {user.name ||
                "Usuario"}
            </strong>

            <div className="scc-person-email">
              {user.email}
            </div>

          </div>

        </div>


        <div className="scc-role-options">

          {roles.map(
            (role) => {

              const checked =
                selected ===
                role.role;


              return (
                <button
                  type="button"
                  key={role.role}
                  className={`scc-role-option ${
                    checked
                      ? "scc-role-option-active"
                      : ""
                  }`}
                  onClick={() =>
                    setSelected(
                      role.role
                    )
                  }
                >

                  <span
                    className={`scc-radio ${
                      checked
                        ? "scc-radio-active"
                        : ""
                    }`}
                  >
                    {checked && (
                      <span />
                    )}
                  </span>


                  <span className="scc-role-option-copy">

                    <strong>
                      {role.name ||
                        role.role}
                    </strong>

                    <small>
                      {role.role ===
                      "admin"
                        ? "Acceso completo al sistema"
                        : `${role.permissions?.length || 0} permisos configurados`}
                    </small>

                  </span>

                </button>
              );
            }
          )}

        </div>


        <div className="scc-modal-actions">

          <button
            type="button"
            className="scc-button-ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancelar
          </button>


          <button
            type="button"
            className="scc-button-primary"
            disabled={
              busy ||
              !selected ||
              selected ===
                user.role
            }
            onClick={() =>
              onApply(
                selected
              )
            }
          >
            {busy
              ? "Aplicando..."
              : "Aplicar cambio"}
          </button>

        </div>

      </section>

    </div>
  );
}


export default function AccessAdminView() {

  const [
    tab,
    setTab,
  ] =
    useState(
      "users"
    );


  const [
    users,
    setUsers,
  ] =
    useState([]);


  const [
    roles,
    setRoles,
  ] =
    useState([]);


  const [
    usersLoading,
    setUsersLoading,
  ] =
    useState(false);


  const [
    rolesLoading,
    setRolesLoading,
  ] =
    useState(false);


  const [
    search,
    setSearch,
  ] =
    useState("");


  const [
    userDrawerOpen,
    setUserDrawerOpen,
  ] =
    useState(false);


  const [
    userForm,
    setUserForm,
  ] =
    useState(
      EMPTY_USER
    );


  const [
    creatingUser,
    setCreatingUser,
  ] =
    useState(false);


  const [
    roleChangeUser,
    setRoleChangeUser,
  ] =
    useState(null);


  const [
    changingRole,
    setChangingRole,
  ] =
    useState(false);


  const [
    roleForm,
    setRoleForm,
  ] =
    useState(
      EMPTY_ROLE
    );


  const [
    roleDrawerOpen,
    setRoleDrawerOpen,
  ] =
    useState(false);


  const [
    selectedRole,
    setSelectedRole,
  ] =
    useState(null);


  const [
    rolePermissions,
    setRolePermissions,
  ] =
    useState([]);


  const [
    savingRole,
    setSavingRole,
  ] =
    useState(false);


  const [
    toast,
    setToast,
  ] =
    useState({
      type: "",
      message: "",
    });


  function showToast(
    type,
    message
  ) {

    setToast({
      type,
      message,
    });


    window.setTimeout(
      () => {

        setToast(
          (current) =>
            current.message ===
            message
              ? {
                  type: "",
                  message: "",
                }
              : current
        );

      },
      4500
    );
  }


  const loadUsers =
    useCallback(
      async () => {

        setUsersLoading(
          true
        );


        try {

          const result =
            await fetchRbacUsers({
              limit: 100,
            });


          setUsers(
            Array.isArray(
              result?.users
            )
              ? result.users
              : []
          );

        } catch (error) {

          console.error(
            error
          );


          showToast(
            "error",
            error?.message ||
              "No fue posible cargar los usuarios."
          );

        } finally {

          setUsersLoading(
            false
          );
        }
      },
      []
    );


  const loadRoles =
    useCallback(
      async () => {

        setRolesLoading(
          true
        );


        try {

          const result =
            await fetchRbacRoles();


          const list =
            Array.isArray(
              result?.roles
            )
              ? result.roles
              : [];


          setRoles(
            list
          );


          setUserForm(
            (current) => {

              if (
                list.some(
                  (role) =>
                    role.role ===
                    current.role
                )
              ) {

                return current;
              }


              return {
                ...current,

                role:
                  list[0]?.role ||
                  "",
              };
            }
          );

        } catch (error) {

          console.error(
            error
          );


          showToast(
            "error",
            error?.message ||
              "No fue posible cargar los roles."
          );

        } finally {

          setRolesLoading(
            false
          );
        }
      },
      []
    );


  useEffect(
    () => {

      loadUsers();
      loadRoles();

    },
    [
      loadUsers,
      loadRoles,
    ]
  );


  const activeRoles =
    useMemo(
      () =>
        roles.filter(
          (role) =>
            role.status !==
            "DISABLED"
        ),
      [
        roles,
      ]
    );


  const filteredUsers =
    useMemo(
      () => {

        const term =
          search
            .trim()
            .toLowerCase();


        if (!term) {
          return users;
        }


        return users.filter(
          (user) =>
            [
              user.name,
              user.email,
              user.role,
              user.status,
              user.sub,
            ]
              .join(" ")
              .toLowerCase()
              .includes(term)
        );
      },
      [
        users,
        search,
      ]
    );


  const metrics =
    useMemo(
      () => {

        const active =
          users.filter(
            (user) =>
              user.status !==
              "DISABLED"
          ).length;


        const admins =
          users.filter(
            (user) =>
              user.role ===
              "admin"
          ).length;


        return {
          users:
            users.length,

          roles:
            roles.length,

          active,

          admins,
        };
      },
      [
        users,
        roles,
      ]
    );


  async function handleCreateUser(
    event
  ) {

    event.preventDefault();


    const name =
      userForm
        .name
        .trim();


    const email =
      userForm
        .email
        .trim()
        .toLowerCase();


    if (!name) {

      showToast(
        "error",
        "Captura el nombre del usuario."
      );

      return;
    }


    if (
      !email ||
      !email.includes("@")
    ) {

      showToast(
        "error",
        "Captura un correo valido."
      );

      return;
    }


    if (!userForm.role) {

      showToast(
        "error",
        "Selecciona un nivel de acceso."
      );

      return;
    }


    setCreatingUser(
      true
    );


    try {

      const result =
        await createRbacUser({
          name,
          email,
          role:
            userForm.role,
        });


      setUserDrawerOpen(
        false
      );


      setUserForm({
        ...EMPTY_USER,

        role:
          activeRoles.some(
            (role) =>
              role.role ===
              "empleados"
          )
            ? "empleados"
            : activeRoles[0]?.role ||
              "",
      });


      await loadUsers();


      showToast(
        "success",
        `Acceso creado para ${
          result?.user?.email ||
          email
        }. Cognito envio la invitacion.`
      );

    } catch (error) {

      console.error(
        error
      );


      if (
        error?.status ===
        409
      ) {

        showToast(
          "error",
          "Ese correo ya tiene un usuario registrado."
        );

      } else {

        showToast(
          "error",
          error?.message ||
            "No fue posible crear el usuario."
        );
      }

    } finally {

      setCreatingUser(
        false
      );
    }
  }


  async function applyUserRole(
    nextRole
  ) {

    const user =
      roleChangeUser;


    if (
      !user ||
      !nextRole ||
      nextRole ===
        user.role
    ) {

      return;
    }


    setChangingRole(
      true
    );


    try {

      await changeRbacUserRole(
        user.sub,
        nextRole
      );


      setUsers(
        (current) =>
          current.map(
            (item) =>
              item.sub ===
              user.sub
                ? {
                    ...item,
                    role:
                      nextRole,
                  }
                : item
          )
      );


      setRoleChangeUser(
        null
      );


      showToast(
        "success",
        `${user.email} ahora tiene el rol ${roleLabel(
          nextRole,
          roles
        )}.`
      );

    } catch (error) {

      console.error(
        error
      );


      showToast(
        "error",
        error?.message ||
          "No fue posible actualizar el rol."
      );

    } finally {

      setChangingRole(
        false
      );
    }
  }


  function selectRole(
    role
  ) {

    setSelectedRole(
      role
    );


    setRolePermissions(
      Array.isArray(
        role.permissions
      )
        ? role.permissions
        : []
    );
  }


  function togglePermission(
    permissionKey
  ) {

    if (
      selectedRole?.role ===
      "admin"
    ) {

      return;
    }


    setRolePermissions(
      (current) => {

        if (
          current.includes(
            permissionKey
          )
        ) {

          return current.filter(
            (item) =>
              item !==
              permissionKey
          );
        }


        return [
          ...current,
          permissionKey,
        ];
      }
    );
  }


  async function handleSaveRole() {

    if (!selectedRole) {
      return;
    }


    setSavingRole(
      true
    );


    try {

      const result =
        await putRbacRole(
          selectedRole.role,
          {
            name:
              selectedRole.name ||
              selectedRole.role,

            status:
              selectedRole.status ||
              "ACTIVE",

            permissions:
              selectedRole.role ===
              "admin"
                ? ["*"]
                : rolePermissions,
          }
        );


      setSelectedRole(
        result
      );


      setRolePermissions(
        Array.isArray(
          result?.permissions
        )
          ? result.permissions
          : []
      );


      await loadRoles();


      showToast(
        "success",
        `Politica de ${
          result?.name ||
          selectedRole.role
        } actualizada.`
      );

    } catch (error) {

      console.error(
        error
      );


      showToast(
        "error",
        error?.message ||
          "No fue posible guardar el rol."
      );

    } finally {

      setSavingRole(
        false
      );
    }
  }


  async function handleCreateRole(
    event
  ) {

    event.preventDefault();


    const key =
      normalizeRoleKey(
        roleForm.key ||
        roleForm.name
      );


    const name =
      roleForm
        .name
        .trim();


    if (
      !key ||
      !name
    ) {

      showToast(
        "error",
        "Captura nombre y clave del rol."
      );

      return;
    }


    if (
      roles.some(
        (role) =>
          role.role ===
          key
      )
    ) {

      showToast(
        "error",
        "Ya existe un rol con esa clave."
      );

      return;
    }


    setSavingRole(
      true
    );


    try {

      const created =
        await putRbacRole(
          key,
          {
            name,

            status:
              "ACTIVE",

            permissions:
              [],
          }
        );


      setRoleForm(
        EMPTY_ROLE
      );


      setRoleDrawerOpen(
        false
      );


      await loadRoles();


      selectRole(
        created
      );


      showToast(
        "success",
        `Rol ${name} creado. Configura ahora sus permisos.`
      );

    } catch (error) {

      console.error(
        error
      );


      showToast(
        "error",
        error?.message ||
          "No fue posible crear el rol."
      );

    } finally {

      setSavingRole(
        false
      );
    }
  }


  const permissionGroups =
    useMemo(
      () => {

        const map =
          new Map();


        ACCESS_CATALOG.forEach(
          (permission) => {

            const group =
              permission.module ||
              "General";


            if (
              !map.has(group)
            ) {

              map.set(
                group,
                []
              );
            }


            map
              .get(group)
              .push(
                permission
              );
          }
        );


        return Array.from(
          map.entries()
        );
      },
      []
    );


  return (
    <div className="scc-page">

      <Toast
        type={toast.type}
        message={toast.message}
        onClose={() =>
          setToast({
            type: "",
            message: "",
          })
        }
      />


      <header className="scc-header">

        <div>

          <div className="scc-eyebrow">
            CONTROL DE IDENTIDAD
          </div>

          <h1 className="scc-title">
            Seguridad y accesos
          </h1>

          <p className="scc-lead">
            Administra identidades,
            niveles de autorización y
            políticas de acceso desde un
            único punto de control.
          </p>

        </div>


        <div className="scc-health">

          <span className="scc-health-dot" />

          <div>

            <strong>
              Servicio operativo
            </strong>

            <span>
              Cognito · RbacAccess
            </span>

          </div>

        </div>

      </header>


      <section className="scc-metrics">

        <div className="scc-metric">

          <strong>
            {metrics.users}
          </strong>

          <span>
            Identidades
          </span>

        </div>


        <div className="scc-metric">

          <strong>
            {metrics.roles}
          </strong>

          <span>
            Roles
          </span>

        </div>


        <div className="scc-metric">

          <strong>
            {metrics.active}
          </strong>

          <span>
            Activos
          </span>

        </div>


        <div className="scc-metric">

          <strong>
            {metrics.admins}
          </strong>

          <span>
            Administradores
          </span>

        </div>

      </section>


      <div className="scc-nav">

        <button
          type="button"
          className={
            tab === "users"
              ? "scc-nav-button scc-nav-active"
              : "scc-nav-button"
          }
          onClick={() =>
            setTab(
              "users"
            )
          }
        >
          Identidades

          <span>
            {metrics.users}
          </span>
        </button>


        <button
          type="button"
          className={
            tab === "roles"
              ? "scc-nav-button scc-nav-active"
              : "scc-nav-button"
          }
          onClick={() =>
            setTab(
              "roles"
            )
          }
        >
          Roles y políticas

          <span>
            {metrics.roles}
          </span>
        </button>

      </div>


      {tab === "users" && (

        <>

          <div className="scc-toolbar">

            <div className="scc-search-wrap">

              <span className="scc-search-icon">
                ⌕
              </span>

              <input
                type="search"
                className="scc-search"
                value={search}
                onChange={(event) =>
                  setSearch(
                    event.target.value
                  )
                }
                placeholder="Buscar por nombre, correo, rol o identificador"
              />

            </div>


            <div className="scc-toolbar-actions">

              <button
                type="button"
                className="scc-button-ghost"
                disabled={usersLoading}
                onClick={loadUsers}
              >
                {usersLoading
                  ? "Actualizando..."
                  : "Actualizar"}
              </button>


              <button
                type="button"
                className="scc-button-primary"
                onClick={() =>
                  setUserDrawerOpen(
                    true
                  )
                }
              >
                <span className="scc-plus">
                  +
                </span>

                Nuevo acceso
              </button>

            </div>

          </div>


          <section className="scc-user-list">

            <div className="scc-list-header">

              <span>
                Identidad
              </span>

              <span>
                Nivel de acceso
              </span>

              <span>
                Estado
              </span>

              <span>
                Último acceso
              </span>

              <span />

            </div>


            {usersLoading &&
            !users.length ? (

              <div className="scc-skeleton-list">

                {[1, 2, 3].map(
                  (item) => (

                    <div
                      className="scc-skeleton-row"
                      key={item}
                    >
                      <div />
                      <div />
                      <div />
                      <div />
                    </div>

                  )
                )}

              </div>

            ) : (

              filteredUsers.map(
                (user) => (

                  <article
                    className="scc-user-row"
                    key={user.sub}
                  >

                    <div className="scc-identity">

                      <div className="scc-avatar">
                        {getInitials(
                          user.name ||
                          user.email
                        )}
                      </div>


                      <div className="scc-identity-copy">

                        <strong>
                          {user.name ||
                            user.email ||
                            "Usuario"}
                        </strong>

                        <span>
                          {user.email}
                        </span>

                        <code>
                          {user.sub}
                        </code>

                      </div>

                    </div>


                    <div>

                      <button
                        type="button"
                        className="scc-role-chip"
                        onClick={() =>
                          setRoleChangeUser(
                            user
                          )
                        }
                      >
                        {roleLabel(
                          user.role,
                          roles
                        )}

                        <span>
                          →
                        </span>
                      </button>

                    </div>


                    <div>

                      <span
                        className={
                          user.status ===
                          "DISABLED"
                            ? "scc-status scc-status-off"
                            : "scc-status"
                        }
                      >
                        <i />

                        {user.status ||
                          "ACTIVE"}
                      </span>

                    </div>


                    <div className="scc-last-access">
                      {formatDate(
                        user.lastLoginAt
                      )}
                    </div>


                    <div className="scc-row-action">

                      <button
                        type="button"
                        className="scc-more"
                        onClick={() =>
                          setRoleChangeUser(
                            user
                          )
                        }
                        aria-label="Administrar usuario"
                      >
                        •••
                      </button>

                    </div>

                  </article>

                )
              )
            )}


            {!usersLoading &&
            !filteredUsers.length && (

              <div className="scc-empty">

                <div className="scc-empty-symbol">
                  ◎
                </div>

                <strong>
                  Sin coincidencias
                </strong>

                <span>
                  No encontramos identidades
                  con ese criterio.
                </span>

              </div>

            )}

          </section>

        </>

      )}


      {tab === "roles" && (

        <div className="scc-policy-layout">

          <aside className="scc-role-sidebar">

            <div className="scc-role-sidebar-head">

              <div>

                <div className="scc-eyebrow">
                  POLITICAS
                </div>

                <h2>
                  Roles
                </h2>

              </div>


              <button
                type="button"
                className="scc-small-add"
                onClick={() =>
                  setRoleDrawerOpen(
                    true
                  )
                }
              >
                +
              </button>

            </div>


            {rolesLoading ? (

              <div className="scc-sidebar-loading">
                Cargando políticas...
              </div>

            ) : (

              <div className="scc-role-list">

                {roles.map(
                  (role) => (

                    <button
                      type="button"
                      key={role.role}
                      className={
                        selectedRole?.role ===
                        role.role
                          ? "scc-role-row scc-role-row-active"
                          : "scc-role-row"
                      }
                      onClick={() =>
                        selectRole(
                          role
                        )
                      }
                    >

                      <span>

                        <strong>
                          {role.name ||
                            role.role}
                        </strong>

                        <small>
                          {role.role ===
                          "admin"
                            ? "Acceso total"
                            : `${role.permissions?.length || 0} permisos`}
                        </small>

                      </span>


                      <span className="scc-role-arrow">
                        ›
                      </span>

                    </button>

                  )
                )}

              </div>

            )}

          </aside>


          <section className="scc-policy-editor">

            {!selectedRole ? (

              <div className="scc-policy-empty">

                <div className="scc-policy-symbol">
                  ◫
                </div>

                <strong>
                  Selecciona una política
                </strong>

                <span>
                  Elige un rol para revisar
                  o modificar sus permisos.
                </span>

              </div>

            ) : (

              <>

                <header className="scc-policy-head">

                  <div>

                    <div className="scc-eyebrow">
                      POLITICA DE ACCESO
                    </div>

                    <h2>
                      {selectedRole.name ||
                        selectedRole.role}
                    </h2>

                    <p>
                      {selectedRole.role ===
                      "admin"
                        ? "Este rol mantiene acceso completo por seguridad."
                        : `${rolePermissions.length} permisos activos`}
                    </p>

                  </div>


                  <div>

                    <span
                      className={
                        selectedRole.status ===
                        "DISABLED"
                          ? "scc-status scc-status-off"
                          : "scc-status"
                      }
                    >
                      <i />

                      {selectedRole.status ||
                        "ACTIVE"}
                    </span>

                  </div>

                </header>


                <div className="scc-permission-groups">

                  {permissionGroups.map(
                    ([
                      group,
                      permissions,
                    ]) => (

                      <section
                        className="scc-permission-section"
                        key={group}
                      >

                        <div className="scc-permission-section-head">

                          <strong>
                            {group}
                          </strong>

                          <span>
                            {
                              permissions.filter(
                                (permission) =>
                                  selectedRole.role ===
                                    "admin" ||
                                  rolePermissions.includes(
                                    permission.key
                                  )
                              ).length
                            }
                            /
                            {
                              permissions.length
                            }
                          </span>

                        </div>


                        <div className="scc-permission-list">

                          {permissions.map(
                            (permission) => {

                              const checked =
                                selectedRole.role ===
                                  "admin" ||
                                rolePermissions.includes(
                                  permission.key
                                );


                              return (
                                <label
                                  className="scc-permission-row"
                                  key={
                                    permission.key
                                  }
                                >

                                  <span
                                    className={
                                      checked
                                        ? "scc-check scc-check-active"
                                        : "scc-check"
                                    }
                                  >

                                    {checked
                                      ? "✓"
                                      : ""}

                                  </span>


                                  <span className="scc-permission-copy">

                                    <strong>
                                      {permission.label}
                                    </strong>

                                    <code>
                                      {permission.key}
                                    </code>

                                  </span>


                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={
                                      selectedRole.role ===
                                      "admin"
                                    }
                                    onChange={() =>
                                      togglePermission(
                                        permission.key
                                      )
                                    }
                                  />

                                </label>
                              );
                            }
                          )}

                        </div>

                      </section>

                    )
                  )}

                </div>


                <footer className="scc-policy-footer">

                  <div>

                    <strong>
                      Cambios de autorización
                    </strong>

                    <span>
                      Las modificaciones afectan
                      menú, rutas y acciones del rol.
                    </span>

                  </div>


                  {selectedRole.role !==
                    "admin" && (

                    <button
                      type="button"
                      className="scc-button-primary"
                      disabled={savingRole}
                      onClick={
                        handleSaveRole
                      }
                    >
                      {savingRole
                        ? "Guardando..."
                        : "Guardar política"}
                    </button>

                  )}

                </footer>

              </>

            )}

          </section>

        </div>

      )}


      <Drawer
        open={userDrawerOpen}
        title="Nuevo acceso"
        eyebrow="ALTA DE IDENTIDAD"
        onClose={() =>
          !creatingUser &&
          setUserDrawerOpen(
            false
          )
        }
      >

        <form
          className="scc-access-form"
          onSubmit={
            handleCreateUser
          }
        >

          <section className="scc-form-section">

            <div className="scc-step-head">

              <span>
                01
              </span>

              <div>

                <strong>
                  Identidad
                </strong>

                <small>
                  Datos con los que
                  reconoceremos al usuario.
                </small>

              </div>

            </div>


            <label className="scc-field">

              <span>
                Nombre completo
              </span>

              <input
                value={
                  userForm.name
                }
                onChange={(event) =>
                  setUserForm(
                    (current) => ({
                      ...current,

                      name:
                        event.target.value,
                    })
                  )
                }
                placeholder="Ej. Roberto Martínez"
                autoComplete="off"
              />

            </label>


            <label className="scc-field">

              <span>
                Correo
              </span>

              <input
                type="email"
                value={
                  userForm.email
                }
                onChange={(event) =>
                  setUserForm(
                    (current) => ({
                      ...current,

                      email:
                        event.target.value,
                    })
                  )
                }
                placeholder="usuario@empresa.com"
                autoComplete="off"
              />

            </label>

          </section>


          <section className="scc-form-section">

            <div className="scc-step-head">

              <span>
                02
              </span>

              <div>

                <strong>
                  Nivel de acceso
                </strong>

                <small>
                  Define qué podrá
                  consultar y operar.
                </small>

              </div>

            </div>


            <div className="scc-access-roles">

              {activeRoles.map(
                (role) => {

                  const selected =
                    userForm.role ===
                    role.role;


                  return (
                    <button
                      type="button"
                      key={role.role}
                      className={
                        selected
                          ? "scc-access-role scc-access-role-active"
                          : "scc-access-role"
                      }
                      onClick={() =>
                        setUserForm(
                          (current) => ({
                            ...current,

                            role:
                              role.role,
                          })
                        )
                      }
                    >

                      <span
                        className={
                          selected
                            ? "scc-radio scc-radio-active"
                            : "scc-radio"
                        }
                      >
                        {selected && (
                          <span />
                        )}
                      </span>


                      <span>

                        <strong>
                          {role.name ||
                            role.role}
                        </strong>

                        <small>
                          {role.role ===
                          "admin"
                            ? "Control completo del sistema"
                            : `${role.permissions?.length || 0} permisos`}
                        </small>

                      </span>

                    </button>
                  );
                }
              )}

            </div>

          </section>


          <section className="scc-form-section">

            <div className="scc-step-head">

              <span>
                03
              </span>

              <div>

                <strong>
                  Invitación
                </strong>

                <small>
                  Cognito gestiona
                  las credenciales.
                </small>

              </div>

            </div>


            <div className="scc-invitation">

              <div className="scc-mail-symbol">
                ↗
              </div>

              <div>

                <strong>
                  Acceso temporal por correo
                </strong>

                <p>
                  Se enviará una invitación
                  al correo capturado.
                  En el primer ingreso el
                  usuario deberá establecer
                  su propia contraseña.
                </p>

              </div>

            </div>

          </section>


          <footer className="scc-drawer-actions">

            <button
              type="button"
              className="scc-button-ghost"
              disabled={creatingUser}
              onClick={() =>
                setUserDrawerOpen(
                  false
                )
              }
            >
              Cancelar
            </button>


            <button
              type="submit"
              className="scc-button-primary"
              disabled={
                creatingUser ||
                rolesLoading
              }
            >
              {creatingUser
                ? "Creando acceso..."
                : "Crear acceso"}
            </button>

          </footer>

        </form>

      </Drawer>


      <Drawer
        open={roleDrawerOpen}
        title="Nuevo rol"
        eyebrow="POLITICA DE ACCESO"
        onClose={() =>
          !savingRole &&
          setRoleDrawerOpen(
            false
          )
        }
      >

        <form
          className="scc-access-form"
          onSubmit={
            handleCreateRole
          }
        >

          <section className="scc-form-section">

            <div className="scc-step-head">

              <span>
                01
              </span>

              <div>

                <strong>
                  Identificación
                </strong>

                <small>
                  Define un nombre
                  reconocible para el rol.
                </small>

              </div>

            </div>


            <label className="scc-field">

              <span>
                Nombre del rol
              </span>

              <input
                value={
                  roleForm.name
                }
                onChange={(event) =>
                  setRoleForm(
                    (current) => ({
                      ...current,

                      name:
                        event.target.value,

                      key:
                        current.key ||
                        normalizeRoleKey(
                          event.target.value
                        ),
                    })
                  )
                }
                placeholder="Ej. Soporte técnico"
              />

            </label>


            <label className="scc-field">

              <span>
                Clave interna
              </span>

              <input
                value={
                  roleForm.key
                }
                onChange={(event) =>
                  setRoleForm(
                    (current) => ({
                      ...current,

                      key:
                        normalizeRoleKey(
                          event.target.value
                        ),
                    })
                  )
                }
                placeholder="soporte"
              />

              <small>
                Esta clave será utilizada
                internamente por RBAC.
              </small>

            </label>

          </section>


          <footer className="scc-drawer-actions">

            <button
              type="button"
              className="scc-button-ghost"
              disabled={savingRole}
              onClick={() =>
                setRoleDrawerOpen(
                  false
                )
              }
            >
              Cancelar
            </button>


            <button
              type="submit"
              className="scc-button-primary"
              disabled={savingRole}
            >
              {savingRole
                ? "Creando..."
                : "Crear rol"}
            </button>

          </footer>

        </form>

      </Drawer>


      <RoleModal
        user={roleChangeUser}
        roles={activeRoles}
        busy={changingRole}
        onClose={() =>
          !changingRole &&
          setRoleChangeUser(
            null
          )
        }
        onApply={
          applyUserRole
        }
      />


      <style>{`

        /* ====================================================
           BASE
        ==================================================== */

        .scc-page {
          max-width: 1480px;
          margin: 0 auto;
          padding: 38px 38px 70px;
          color: #151922;
          font-family:
            Inter,
            system-ui,
            -apple-system,
            BlinkMacSystemFont,
            "Segoe UI",
            sans-serif;
        }

        .scc-page * {
          box-sizing: border-box;
        }

        button,
        input {
          font: inherit;
        }


        /* ====================================================
           HEADER
        ==================================================== */

        .scc-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 30px;
          margin-bottom: 34px;
        }

        .scc-eyebrow {
          color: #7d8490;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.16em;
        }

        .scc-title {
          margin: 7px 0 8px;
          font-size: clamp(28px, 3vw, 38px);
          line-height: 1.1;
          font-weight: 650;
          letter-spacing: -0.035em;
        }

        .scc-lead {
          max-width: 680px;
          margin: 0;
          color: #707783;
          font-size: 14px;
          line-height: 1.65;
        }

        .scc-health {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 9px 12px;
          border: 1px solid #e0e4e9;
          border-radius: 8px;
          background: rgba(255,255,255,0.55);
        }

        .scc-health-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #25a46a;
          box-shadow:
            0 0 0 4px rgba(37,164,106,0.10);
        }

        .scc-health strong {
          display: block;
          font-size: 11px;
          font-weight: 750;
        }

        .scc-health span {
          display: block;
          margin-top: 2px;
          color: #959ba4;
          font-size: 9px;
        }


        /* ====================================================
           METRICS
        ==================================================== */

        .scc-metrics {
          display: grid;
          grid-template-columns:
            repeat(4, minmax(0, 1fr));
          margin-bottom: 31px;
          border-top: 1px solid #e2e6ea;
          border-bottom: 1px solid #e2e6ea;
        }

        .scc-metric {
          position: relative;
          padding: 23px 24px;
        }

        .scc-metric:not(:last-child)::after {
          content: "";
          position: absolute;
          top: 18px;
          right: 0;
          bottom: 18px;
          width: 1px;
          background: #e3e7eb;
        }

        .scc-metric strong {
          display: block;
          font-size: 27px;
          font-weight: 620;
          letter-spacing: -0.04em;
        }

        .scc-metric span {
          display: block;
          margin-top: 5px;
          color: #8a9099;
          font-size: 11px;
          font-weight: 650;
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }


        /* ====================================================
           NAV
        ==================================================== */

        .scc-nav {
          display: flex;
          gap: 26px;
          margin-bottom: 25px;
          border-bottom: 1px solid #e3e6ea;
        }

        .scc-nav-button {
          position: relative;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0 2px 13px;
          border: 0;
          background: transparent;
          color: #858b94;
          cursor: pointer;
          font-size: 13px;
          font-weight: 700;
        }

        .scc-nav-button span {
          min-width: 20px;
          padding: 2px 6px;
          border-radius: 999px;
          background: #eceff2;
          color: #7d838d;
          font-size: 9px;
        }

        .scc-nav-active {
          color: #151922;
        }

        .scc-nav-active::after {
          content: "";
          position: absolute;
          right: 0;
          bottom: -1px;
          left: 0;
          height: 2px;
          background: #151922;
        }


        /* ====================================================
           TOOLBAR
        ==================================================== */

        .scc-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 15px;
        }

        .scc-search-wrap {
          position: relative;
          width: min(560px, 100%);
        }

        .scc-search-icon {
          position: absolute;
          top: 50%;
          left: 14px;
          color: #9aa0aa;
          font-size: 18px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .scc-search {
          width: 100%;
          height: 42px;
          padding: 0 15px 0 42px;
          border: 1px solid #dadfe5;
          border-radius: 7px;
          outline: none;
          background: #fff;
          color: #181c24;
          font-size: 12px;
          transition:
            border-color 150ms ease,
            box-shadow 150ms ease;
        }

        .scc-search:focus {
          border-color: #9da5af;
          box-shadow:
            0 0 0 3px rgba(17,24,39,0.045);
        }

        .scc-toolbar-actions {
          display: flex;
          gap: 9px;
        }


        /* ====================================================
           BUTTONS
        ==================================================== */

        .scc-button-primary,
        .scc-button-ghost {
          min-height: 40px;
          padding: 0 15px;
          border-radius: 7px;
          cursor: pointer;
          font-size: 12px;
          font-weight: 750;
          transition:
            transform 120ms ease,
            background 120ms ease,
            border-color 120ms ease;
        }

        .scc-button-primary {
          border: 1px solid #151922;
          background: #151922;
          color: #fff;
        }

        .scc-button-primary:hover:not(:disabled) {
          background: #252a33;
          transform: translateY(-1px);
        }

        .scc-button-primary:disabled {
          cursor: not-allowed;
          opacity: 0.48;
        }

        .scc-button-ghost {
          border: 1px solid #dce0e5;
          background: #fff;
          color: #363c45;
        }

        .scc-button-ghost:hover:not(:disabled) {
          border-color: #bcc3cb;
          background: #fafbfc;
        }

        .scc-plus {
          display: inline-block;
          margin-right: 7px;
          font-size: 16px;
          font-weight: 400;
          line-height: 0;
        }


        /* ====================================================
           USER LIST
        ==================================================== */

        .scc-user-list {
          overflow: hidden;
          border-top: 1px solid #dfe3e8;
          border-bottom: 1px solid #dfe3e8;
        }

        .scc-list-header,
        .scc-user-row {
          display: grid;
          grid-template-columns:
            minmax(330px, 2fr)
            minmax(155px, 0.85fr)
            115px
            minmax(175px, 1fr)
            46px;
          column-gap: 18px;
          align-items: center;
        }

        .scc-list-header {
          min-height: 41px;
          padding: 0 10px;
          color: #989ea7;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.07em;
          text-transform: uppercase;
        }

        .scc-user-row {
          min-height: 78px;
          padding: 12px 10px;
          border-top: 1px solid #edf0f2;
          transition:
            background 130ms ease;
        }

        .scc-user-row:hover {
          background: rgba(255,255,255,0.7);
        }

        .scc-identity {
          display: flex;
          align-items: center;
          min-width: 0;
          gap: 13px;
        }

        .scc-avatar {
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          width: 39px;
          height: 39px;
          border: 1px solid #dadfe5;
          border-radius: 9px;
          background:
            linear-gradient(
              145deg,
              #ffffff,
              #f1f3f5
            );
          color: #464c55;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.04em;
        }

        .scc-avatar-large {
          width: 48px;
          height: 48px;
          font-size: 12px;
        }

        .scc-identity-copy {
          min-width: 0;
        }

        .scc-identity-copy strong {
          display: block;
          overflow: hidden;
          color: #191d25;
          font-size: 12px;
          font-weight: 720;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .scc-identity-copy span {
          display: block;
          overflow: hidden;
          margin-top: 3px;
          color: #767d87;
          font-size: 11px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .scc-identity-copy code {
          display: block;
          overflow: hidden;
          max-width: 315px;
          margin-top: 4px;
          color: #adb2b9;
          font-family:
            "SFMono-Regular",
            Consolas,
            monospace;
          font-size: 8px;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .scc-role-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 9px;
          border: 0;
          border-radius: 6px;
          background: #f0f2f5;
          color: #353b44;
          cursor: pointer;
          font-size: 10px;
          font-weight: 730;
        }

        .scc-role-chip span {
          color: #a0a6ae;
          transition:
            transform 120ms ease;
        }

        .scc-role-chip:hover span {
          transform: translateX(2px);
        }

        .scc-status {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: #37775a;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: 0.05em;
        }

        .scc-status i {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #34a56e;
        }

        .scc-status-off {
          color: #8a9098;
        }

        .scc-status-off i {
          background: #aeb3ba;
        }

        .scc-last-access {
          color: #606771;
          font-size: 10px;
        }

        .scc-row-action {
          text-align: right;
        }

        .scc-more {
          width: 35px;
          height: 35px;
          border: 0;
          border-radius: 6px;
          background: transparent;
          color: #8d939d;
          cursor: pointer;
          letter-spacing: 2px;
        }

        .scc-more:hover {
          background: #eceff2;
          color: #252a32;
        }


        /* ====================================================
           EMPTY + SKELETON
        ==================================================== */

        .scc-empty {
          display: grid;
          justify-items: center;
          gap: 5px;
          padding: 58px 20px;
          border-top: 1px solid #edf0f2;
          color: #777e88;
          text-align: center;
        }

        .scc-empty strong {
          color: #343a43;
          font-size: 13px;
        }

        .scc-empty span {
          font-size: 11px;
        }

        .scc-empty-symbol {
          margin-bottom: 7px;
          color: #b2b7be;
          font-size: 26px;
        }

        .scc-skeleton-row {
          display: grid;
          grid-template-columns:
            2fr 1fr 0.7fr 1fr;
          gap: 30px;
          padding: 24px 10px;
          border-top: 1px solid #edf0f2;
        }

        .scc-skeleton-row div {
          height: 10px;
          border-radius: 4px;
          background:
            linear-gradient(
              90deg,
              #eceff2 25%,
              #f5f6f7 50%,
              #eceff2 75%
            );
          background-size: 200% 100%;
          animation:
            scc-shimmer 1.2s infinite;
        }


        /* ====================================================
           POLICY
        ==================================================== */

        .scc-policy-layout {
          display: grid;
          grid-template-columns:
            285px minmax(0, 1fr);
          min-height: 620px;
          border-top: 1px solid #e1e5e9;
          border-bottom: 1px solid #e1e5e9;
        }

        .scc-role-sidebar {
          padding: 23px 18px 23px 0;
          border-right: 1px solid #e2e6ea;
        }

        .scc-role-sidebar-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 18px;
          padding: 0 8px;
        }

        .scc-role-sidebar-head h2 {
          margin: 5px 0 0;
          font-size: 17px;
          font-weight: 670;
        }

        .scc-small-add {
          display: grid;
          place-items: center;
          width: 31px;
          height: 31px;
          border: 1px solid #d7dce2;
          border-radius: 6px;
          background: #fff;
          cursor: pointer;
          font-size: 17px;
        }

        .scc-role-list {
          display: grid;
          gap: 2px;
        }

        .scc-role-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 11px 10px;
          border: 0;
          border-radius: 6px;
          background: transparent;
          color: #636a74;
          cursor: pointer;
          text-align: left;
        }

        .scc-role-row strong {
          display: block;
          color: inherit;
          font-size: 11px;
          font-weight: 720;
        }

        .scc-role-row small {
          display: block;
          margin-top: 3px;
          color: #a0a5ad;
          font-size: 9px;
        }

        .scc-role-row:hover {
          background: #f0f2f4;
        }

        .scc-role-row-active {
          background: #181d25 !important;
          color: #fff;
        }

        .scc-role-row-active small,
        .scc-role-row-active .scc-role-arrow {
          color: #979da7;
        }

        .scc-role-arrow {
          color: #b1b6bc;
          font-size: 20px;
          font-weight: 300;
        }

        .scc-sidebar-loading {
          padding: 30px 10px;
          color: #9298a1;
          font-size: 10px;
        }

        .scc-policy-editor {
          padding: 28px 0 28px 31px;
        }

        .scc-policy-empty {
          display: grid;
          place-items: center;
          align-content: center;
          min-height: 540px;
          color: #8a9099;
          text-align: center;
        }

        .scc-policy-empty strong {
          margin-top: 12px;
          color: #343941;
          font-size: 13px;
        }

        .scc-policy-empty span {
          max-width: 300px;
          margin-top: 5px;
          font-size: 11px;
          line-height: 1.5;
        }

        .scc-policy-symbol {
          color: #b7bcc3;
          font-size: 30px;
        }

        .scc-policy-head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding-bottom: 23px;
          border-bottom: 1px solid #e6e9ec;
        }

        .scc-policy-head h2 {
          margin: 5px 0 4px;
          font-size: 23px;
          font-weight: 650;
          letter-spacing: -0.025em;
        }

        .scc-policy-head p {
          margin: 0;
          color: #838a94;
          font-size: 11px;
        }

        .scc-permission-groups {
          display: grid;
          gap: 27px;
          padding: 26px 0;
        }

        .scc-permission-section-head {
          display: flex;
          justify-content: space-between;
          margin-bottom: 7px;
          padding: 0 5px;
        }

        .scc-permission-section-head strong {
          color: #525963;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.075em;
          text-transform: uppercase;
        }

        .scc-permission-section-head span {
          color: #a1a6ae;
          font-size: 9px;
        }

        .scc-permission-list {
          border-top: 1px solid #e8ebee;
        }

        .scc-permission-row {
          display: grid;
          grid-template-columns:
            26px minmax(0,1fr) 22px;
          align-items: center;
          gap: 8px;
          min-height: 49px;
          padding: 7px 5px;
          border-bottom: 1px solid #eef0f2;
          cursor: pointer;
        }

        .scc-permission-row:hover {
          background: rgba(255,255,255,0.65);
        }

        .scc-permission-row input {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }

        .scc-check {
          display: grid;
          place-items: center;
          width: 18px;
          height: 18px;
          border: 1px solid #ced3d9;
          border-radius: 4px;
          color: #fff;
          font-size: 9px;
          font-weight: 800;
        }

        .scc-check-active {
          border-color: #1c2129;
          background: #1c2129;
        }

        .scc-permission-copy strong {
          display: block;
          color: #343a43;
          font-size: 11px;
          font-weight: 650;
        }

        .scc-permission-copy code {
          display: block;
          margin-top: 2px;
          color: #a1a6ae;
          font-family:
            Consolas,
            monospace;
          font-size: 8px;
        }

        .scc-policy-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 20px;
          padding-top: 20px;
          border-top: 1px solid #e3e6ea;
        }

        .scc-policy-footer strong {
          display: block;
          font-size: 11px;
        }

        .scc-policy-footer span {
          display: block;
          margin-top: 3px;
          color: #8e949d;
          font-size: 9px;
        }


        /* ====================================================
           OVERLAY / DRAWER / MODAL
        ==================================================== */

        .scc-overlay {
          position: fixed;
          inset: 0;
          z-index: 99990;
          display: flex;
          justify-content: flex-end;
          background: rgba(16,21,29,0.27);
          backdrop-filter: blur(2px);
        }

        .scc-overlay-centered {
          align-items: center;
          justify-content: center;
        }

        .scc-overlay-dismiss {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
          background: transparent;
          cursor: default;
        }

        .scc-drawer {
          position: relative;
          z-index: 2;
          width: min(470px, 94vw);
          height: 100%;
          overflow-y: auto;
          background: #fbfbfc;
          box-shadow:
            -18px 0 55px rgba(14,19,26,0.13);
          animation:
            scc-drawer-in 220ms ease-out both;
        }

        .scc-drawer-head {
          position: sticky;
          top: 0;
          z-index: 5;
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          padding: 28px 30px 21px;
          border-bottom: 1px solid #e4e7ea;
          background:
            rgba(251,251,252,0.94);
          backdrop-filter: blur(12px);
        }

        .scc-drawer-title {
          margin: 6px 0 0;
          font-size: 24px;
          font-weight: 650;
          letter-spacing: -0.03em;
        }

        .scc-drawer-body {
          padding: 0 30px 35px;
        }

        .scc-icon-button {
          display: grid;
          place-items: center;
          width: 33px;
          height: 33px;
          border: 1px solid #e0e4e8;
          border-radius: 7px;
          background: #fff;
          color: #727983;
          cursor: pointer;
          font-size: 20px;
          font-weight: 300;
        }

        .scc-modal {
          position: relative;
          z-index: 2;
          width: min(460px, calc(100vw - 36px));
          max-height: calc(100vh - 50px);
          overflow-y: auto;
          border-radius: 11px;
          background: #fbfbfc;
          box-shadow:
            0 22px 75px rgba(14,19,26,0.22);
          animation:
            scc-modal-in 180ms ease-out both;
        }

        .scc-modal-head {
          display: flex;
          justify-content: space-between;
          padding: 25px 26px 20px;
          border-bottom: 1px solid #e5e8eb;
        }

        .scc-modal-title {
          margin: 5px 0 0;
          font-size: 19px;
          font-weight: 650;
        }

        .scc-person-summary {
          display: flex;
          align-items: center;
          gap: 13px;
          margin: 21px 25px 16px;
          padding: 13px;
          border: 1px solid #e5e8eb;
          border-radius: 8px;
          background: #fff;
        }

        .scc-person-name {
          display: block;
          font-size: 12px;
        }

        .scc-person-email {
          margin-top: 3px;
          color: #858b94;
          font-size: 10px;
        }

        .scc-role-options {
          display: grid;
          gap: 6px;
          padding: 0 25px;
        }

        .scc-role-option,
        .scc-access-role {
          display: flex;
          align-items: center;
          gap: 11px;
          width: 100%;
          padding: 12px;
          border: 1px solid #e1e5e9;
          border-radius: 8px;
          background: #fff;
          color: #434a54;
          cursor: pointer;
          text-align: left;
        }

        .scc-role-option:hover,
        .scc-access-role:hover {
          border-color: #bcc3cb;
        }

        .scc-role-option-active,
        .scc-access-role-active {
          border-color: #252b33;
          box-shadow:
            0 0 0 1px #252b33 inset;
        }

        .scc-radio {
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          width: 16px;
          height: 16px;
          border: 1px solid #c9cfd5;
          border-radius: 50%;
        }

        .scc-radio-active {
          border-color: #20252d;
        }

        .scc-radio-active span {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #20252d;
        }

        .scc-role-option-copy,
        .scc-access-role > span:last-child {
          display: block;
        }

        .scc-role-option-copy strong,
        .scc-access-role strong {
          display: block;
          color: #333a43;
          font-size: 11px;
        }

        .scc-role-option-copy small,
        .scc-access-role small {
          display: block;
          margin-top: 3px;
          color: #9197a0;
          font-size: 9px;
        }

        .scc-modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin-top: 22px;
          padding: 18px 25px 23px;
          border-top: 1px solid #e6e9ec;
        }


        /* ====================================================
           CREATE USER FORM
        ==================================================== */

        .scc-access-form {
          display: grid;
        }

        .scc-form-section {
          padding: 25px 0;
          border-bottom: 1px solid #e5e8eb;
        }

        .scc-step-head {
          display: flex;
          align-items: flex-start;
          gap: 11px;
          margin-bottom: 20px;
        }

        .scc-step-head > span {
          flex: 0 0 auto;
          color: #a1a6ae;
          font-family:
            Consolas,
            monospace;
          font-size: 9px;
          font-weight: 700;
        }

        .scc-step-head strong {
          display: block;
          font-size: 12px;
          font-weight: 720;
        }

        .scc-step-head small {
          display: block;
          margin-top: 3px;
          color: #959ba4;
          font-size: 9px;
          line-height: 1.45;
        }

        .scc-field {
          display: grid;
          gap: 7px;
          margin-bottom: 15px;
        }

        .scc-field:last-child {
          margin-bottom: 0;
        }

        .scc-field > span {
          color: #555c66;
          font-size: 10px;
          font-weight: 720;
        }

        .scc-field > small {
          color: #9da2aa;
          font-size: 9px;
        }

        .scc-field input {
          width: 100%;
          height: 42px;
          padding: 0 12px;
          border: 1px solid #d9dee4;
          border-radius: 7px;
          outline: none;
          background: #fff;
          color: #232831;
          font-size: 11px;
        }

        .scc-field input:focus {
          border-color: #929aa4;
          box-shadow:
            0 0 0 3px rgba(17,24,39,0.04);
        }

        .scc-access-roles {
          display: grid;
          gap: 7px;
        }

        .scc-invitation {
          display: flex;
          gap: 12px;
          padding: 14px;
          border: 1px solid #e2e6ea;
          border-radius: 8px;
          background: #fff;
        }

        .scc-mail-symbol {
          flex: 0 0 auto;
          display: grid;
          place-items: center;
          width: 31px;
          height: 31px;
          border-radius: 6px;
          background: #f0f2f4;
          color: #555c66;
          font-size: 14px;
        }

        .scc-invitation strong {
          display: block;
          font-size: 10px;
        }

        .scc-invitation p {
          margin: 4px 0 0;
          color: #858b94;
          font-size: 9px;
          line-height: 1.55;
        }

        .scc-drawer-actions {
          position: sticky;
          bottom: -35px;
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          margin: 0 -30px;
          padding: 18px 30px;
          border-top: 1px solid #e1e5e9;
          background:
            rgba(251,251,252,0.95);
          backdrop-filter: blur(12px);
        }


        /* ====================================================
           TOAST
        ==================================================== */

        .scc-toast {
          position: fixed;
          z-index: 100000;
          top: 22px;
          right: 24px;
          display: grid;
          grid-template-columns:
            28px minmax(0,1fr) 27px;
          align-items: center;
          gap: 8px;
          width: min(390px, calc(100vw - 32px));
          padding: 10px 10px 10px 12px;
          border: 1px solid #dce1e6;
          border-radius: 8px;
          background: #fff;
          box-shadow:
            0 14px 40px rgba(15,20,27,0.13);
          animation:
            scc-toast-in 180ms ease-out both;
        }

        .scc-toast-icon {
          display: grid;
          place-items: center;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #e9f6ef;
          color: #25865b;
          font-size: 11px;
          font-weight: 900;
        }

        .scc-toast-error .scc-toast-icon {
          background: #faeeee;
          color: #b64b4b;
        }

        .scc-toast-message {
          color: #444b55;
          font-size: 10px;
          line-height: 1.45;
        }

        .scc-toast-close {
          border: 0;
          background: transparent;
          color: #9ba1aa;
          cursor: pointer;
          font-size: 17px;
        }


        /* ====================================================
           MOTION
        ==================================================== */

        @keyframes scc-drawer-in {
          from {
            opacity: 0;
            transform: translateX(18px);
          }

          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes scc-modal-in {
          from {
            opacity: 0;
            transform:
              translateY(6px)
              scale(0.99);
          }

          to {
            opacity: 1;
            transform:
              translateY(0)
              scale(1);
          }
        }

        @keyframes scc-toast-in {
          from {
            opacity: 0;
            transform: translateY(-7px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scc-shimmer {
          0% {
            background-position:
              200% 0;
          }

          100% {
            background-position:
              -200% 0;
          }
        }


        /* ====================================================
           RESPONSIVE
        ==================================================== */

        @media (
          max-width: 1050px
        ) {

          .scc-list-header {
            display: none;
          }

          .scc-user-row {
            grid-template-columns:
              minmax(280px, 1fr)
              140px
              90px
              40px;
          }

          .scc-last-access {
            display: none;
          }

        }


        @media (
          max-width: 820px
        ) {

          .scc-page {
            padding:
              25px 18px 50px;
          }

          .scc-header {
            display: grid;
          }

          .scc-health {
            width: max-content;
          }

          .scc-metrics {
            grid-template-columns:
              repeat(2,1fr);
          }

          .scc-metric:nth-child(2)::after {
            display: none;
          }

          .scc-toolbar {
            display: grid;
          }

          .scc-toolbar-actions {
            justify-content:
              flex-end;
          }

          .scc-policy-layout {
            grid-template-columns:
              1fr;
          }

          .scc-role-sidebar {
            padding:
              20px 0;
            border-right: 0;
            border-bottom:
              1px solid #e2e6ea;
          }

          .scc-policy-editor {
            padding:
              25px 0;
          }

        }


        @media (
          max-width: 620px
        ) {

          .scc-user-row {
            grid-template-columns:
              minmax(0,1fr)
              40px;
            gap: 10px;
          }

          .scc-user-row >
          div:nth-child(2),
          .scc-user-row >
          div:nth-child(3),
          .scc-user-row >
          div:nth-child(4) {
            display: none;
          }

          .scc-metrics {
            grid-template-columns:
              repeat(2,1fr);
          }

          .scc-nav {
            gap: 16px;
          }

          .scc-policy-head {
            gap: 20px;
          }

        }


        @media (
          prefers-reduced-motion:
          reduce
        ) {

          .scc-drawer,
          .scc-modal,
          .scc-toast,
          .scc-skeleton-row div {
            animation: none !important;
          }

        }

      `}</style>

    </div>
  );
}